import React from 'react';
import { Button as AntButton, type ButtonProps } from 'antd';
import { useTheme } from '@emotion/react';
import CommonButton, { type ButtonSize, type CustomButtonProps } from 'components/common/button';

// Every difference between the two prop surfaces is a place a migrated call site could silently
// change behaviour, so each is spelled out rather than spread through.
export const toCommonProps = ({
  loading,
  danger,
  type,
  size,
  shape,
  target,
  ...rest
}: ButtonProps): CustomButtonProps => ({
  ...rest,
  // antd puts `target` on the anchor it renders from `href`. The wrapper has no `target`, so left in
  // rest it lands on the inner <button>, where it does nothing and the link opens in the same tab.
  // `isExternalLink` is how the wrapper says "real anchor, new tab".
  isExternalLink: target === '_blank' || undefined,
  // antd's `default` shape is the wrapper's unset; only circle and round are real variants.
  shape: shape === 'default' ? undefined : shape,
  // antd carries `danger` as a boolean alongside `type`; the wrapper folds it into one union. `text`
  // is the wrapper's `link`. `dashed` and `ghost` are not in the design system, so they degrade.
  type: danger ? 'danger' : type === 'text' ? 'link' : type === 'primary' || type === 'link' ? type : 'default',
  // antd's middle is the wrapper's unset. Passing it through would be an unknown size.
  size: size === 'middle' ? undefined : (size as ButtonSize | undefined),
  isLoading: typeof loading === 'object' ? true : Boolean(loading)
});

// Bridged sites take antd's type union, so Tertiary is antd's `text` here. Undefined switch-off.
export const newDesignAntdType = (theme: { isNewDesign: boolean }, type: ButtonProps['type']) =>
  theme.isNewDesign ? type : undefined;

// `_blank` is the only target the new-design path can honour (toCommonProps folds it into
// `isExternalLink`); any other value would work switch-off and silently vanish switch-on, so the
// prop surface rejects it at compile time instead.
export type BridgedButtonProps = Omit<ButtonProps, 'target'> & { target?: '_blank' };

/**
 * For call sites importing Button straight from antd. Legacy renders antd's Button verbatim; the new
 * design routes through the wrapper. Repointing them outright is not possible - the wrapper's legacy
 * path adds a min-width and padding a raw-antd site never had, which would move switch-off.
 *
 * A migration step with a deletion date, not a second Button: APO-484 removes the branch and leaves
 * the site on the wrapper. This is the grep for that sweep.
 *
 * The ref reaches the antd button on the legacy path only - the common wrapper forwards none, so
 * switch-on parents fall back the same way they do around every common Button today.
 */
export const BridgedButton = Object.assign(
  React.forwardRef<HTMLButtonElement | HTMLAnchorElement, BridgedButtonProps>(function BridgedButton(props, ref) {
    const theme = useTheme();

    return theme.isNewDesign ? <CommonButton {...toCommonProps(props)} /> : <AntButton ref={ref} {...props} />;
  }),
  // antd Tooltip's disabled-button compat checks this static on the child's type (tooltip/index.js in
  // antd 5.6.4); hidden behind the bridge, a disabled button's tooltip stops firing on hover.
  { __ANT_BUTTON: true }
);

export default BridgedButton;
